import React, { useState } from 'react';

import AnimateHeight from 'react-animate-height';
import ConditionalWrapper from '~/components/utils/ConditionalWrapper';
import Icons from '~/components/ui/Icons';
import PropTypes from 'prop-types';
import cn from 'classnames';

/* Input
 * Possible styles: primary, secondary, text
 */

const Input = ({
  attrs = {},
  className,
  input = {},
  attachedButton,
  onValue,
  onSubmit,
  icon = {},
  fullWidthBreakpoint = 'sm',
}) => {
  const {
    type: iconType,
    onClick: iconOnClick,
    className: iconClassName,
  } = icon;
  const { id, type = 'text', checked = false, value = '' } = attrs;
  const { error, label } = input;
  const {
    className: inputClassName,
    labelClassName = '',
    ...inputAttrs
  } = attrs;
  const isCheckable = ['checkbox', 'radio'].includes(type);

  const [currentValue, setCurrentValue] = useState(
    isCheckable ? checked : value,
  );

  const onKeyDown = event => {
    if (event.key === 'Enter') {
      onSubmit(event);
    }
  };

  const onChange = event => {
    const newValue = isCheckable ? event.target.checked : event.target.value;

    setCurrentValue(newValue);

    if (onValue) {
      onValue(newValue);
    }
  };

  if (label && !id) {
    return new Error(
      `Prop 'attrs[id]' is required when prop 'input[label]' is provided in 'Input'.`,
    );
  }

  return (
    <div
      className={cn(
        `input-group relative w-full`,
        className,
        !isCheckable && `${fullWidthBreakpoint}:w-auto`,
        {
          'input-group--checkbox': type === 'checkbox',
          'input-group--radio': type === 'radio',
        },
      )}
    >
      {label && (
        <label htmlFor={id} className={labelClassName}>
          {label}
        </label>
      )}

      <ConditionalWrapper
        condition={!!attachedButton}
        wrapper={children => (
          <div
            className={cn('input--attachment-container flex-col sm:flex-row', {
              'w-full': !isCheckable,
            })}
          >
            {children}
          </div>
        )}
      >
        {isCheckable && checked && (
          <div className="icon-check-container">
            <Icons type="check" />
          </div>
        )}

        <div
          className={cn('relative leading-none', {
            'mb-3 sm:mb-0 mr-0 sm:mr-5': attachedButton,
            'flex-grow': attachedButton && !isCheckable,
            'w-full': !isCheckable,
          })}
        >
          <input
            className={cn(
              'input',
              inputClassName,
              {
                'input--error': !!error,
                'pr-6': !!iconType,
              },
              'cursor-pointer',
            )}
            {...inputAttrs}
            value={currentValue}
            onChange={onChange}
            onKeyDown={onKeyDown}
          />

          {iconType && (
            <Icons
              type={iconType}
              className={cn('absolute center-vertical right-1', iconClassName)}
              onClick={iconOnClick}
            />
          )}
        </div>
        {attachedButton}
      </ConditionalWrapper>

      {!isCheckable && (
        <AnimateHeight height={error ? 'auto' : 0} className="text-left">
          <span className="input__error">{error}</span>
        </AnimateHeight>
      )}
    </div>
  );
};

Input.propTypes = {
  attrs: PropTypes.shape({
    placeholder: PropTypes.string,
  }),
  className: PropTypes.string,
  input: PropTypes.shape({
    arrow: PropTypes.bool,
    label: PropTypes.string,
    error: PropTypes.string,
  }),
  attachedButton: PropTypes.node,
  fullWidthBreakpoint: PropTypes.string,
};

export default Input;
